import { useEffect, useRef, useState } from 'react';
import logo from './assets/images/logo.png';
import ga from './assets/images/ga.png';
import './App.css';

const Copyright = () => {
  return '©2000 - ' + new Date().getFullYear();
};

const SECTION_IDS = ['home', 'capability', 'network', 'contact'];

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const activeSectionRef = useRef('home');
  const wheelLockRef = useRef(false);
  const wheelUnlockTimerRef = useRef(null);

  useEffect(() => {
    document.title = '闪石信息科技 | Ocean Container Intelligence';

    const updateActiveSectionByScroll = () => {
      const nav = document.querySelector('.nav-shell');
      if (nav) {
        if (window.scrollY > 40) {
          nav.classList.add('is-scrolled');
        } else {
          nav.classList.remove('is-scrolled');
        }
      }

      const viewportProbe = window.scrollY + window.innerHeight * 0.45;
      let current = SECTION_IDS[0];

      SECTION_IDS.forEach((id) => {
        const section = document.getElementById(id);
        if (section && section.offsetTop <= viewportProbe) {
          current = id;
        }
      });

      if (current !== activeSectionRef.current) {
        activeSectionRef.current = current;
        setActiveSection(current);
      }
    };

    const scrollToSection = (id) => {
      const target = document.getElementById(id);
      if (!target) {
        return;
      }
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      activeSectionRef.current = id;
      setActiveSection(id);
    };

    const onWheel = (event) => {
      if (Math.abs(event.deltaY) < 12) {
        return;
      }

      event.preventDefault();

      if (wheelLockRef.current) {
        return;
      }

      const direction = event.deltaY > 0 ? 1 : -1;
      let currentIndex = SECTION_IDS.indexOf(activeSectionRef.current);

      if (currentIndex < 0) {
        const currentScroll = window.scrollY;
        currentIndex = 0;
        let nearestDistance = Infinity;
        SECTION_IDS.forEach((id, index) => {
          const section = document.getElementById(id);
          if (!section) {
            return;
          }
          const distance = Math.abs(section.offsetTop - currentScroll);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            currentIndex = index;
          }
        });
      }

      const nextIndex = Math.min(SECTION_IDS.length - 1, Math.max(0, currentIndex + direction));
      if (nextIndex === currentIndex) {
        return;
      }

      wheelLockRef.current = true;
      scrollToSection(SECTION_IDS[nextIndex]);

      if (wheelUnlockTimerRef.current) {
        window.clearTimeout(wheelUnlockTimerRef.current);
      }
      wheelUnlockTimerRef.current = window.setTimeout(() => {
        wheelLockRef.current = false;
      }, 900);
    };

    updateActiveSectionByScroll();
    window.addEventListener('scroll', updateActiveSectionByScroll);
    window.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      window.removeEventListener('scroll', updateActiveSectionByScroll);
      window.removeEventListener('wheel', onWheel);
      if (wheelUnlockTimerRef.current) {
        window.clearTimeout(wheelUnlockTimerRef.current);
      }
    };
  }, []);

  const handleNavClick = (event, id) => {
    event.preventDefault();
    const target = document.getElementById(id);
    if (!target) {
      return;
    }
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    activeSectionRef.current = id;
    setActiveSection(id);
  };

  return (
    <div className="App">
      <div className="deep-ocean-bg" aria-hidden="true">
        <div className="ocean-glow-layer"></div>
        <div className="ocean-grid-layer"></div>
        <div className="ocean-fog-layer"></div>
        <div className="ship-fleet fleet-near">
          <div className="cargo-ship ship-a">
            <i className="ship-hull"></i>
            <i className="containers"></i>
            <i className="containers top"></i>
          </div>
          <div className="cargo-ship ship-b">
            <i className="ship-hull"></i>
            <i className="containers"></i>
            <i className="containers top"></i>
          </div>
        </div>
        <div className="ship-fleet fleet-far">
          <div className="cargo-ship ship-c">
            <i className="ship-hull"></i>
            <i className="containers"></i>
            <i className="containers top"></i>
          </div>
        </div>
        <div className="water-waves"></div>
      </div>

      <nav className="nav-shell">
        <div className="nav-inner">
          <a href="#home" className="brand">
            <img src={logo} alt="闪石科技" />
            <span>闪石信息科技</span>
          </a>
          <div className="nav-links">
            <a href="#home" className={activeSection === 'home' ? 'active' : ''} onClick={(event) => handleNavClick(event, 'home')}>首页</a>
            <a href="#capability" className={activeSection === 'capability' ? 'active' : ''} onClick={(event) => handleNavClick(event, 'capability')}>能力</a>
            <a href="#network" className={activeSection === 'network' ? 'active' : ''} onClick={(event) => handleNavClick(event, 'network')}>航线网络</a>
            <a href="#contact" className={activeSection === 'contact' ? 'active' : ''} onClick={(event) => handleNavClick(event, 'contact')}>联系</a>
          </div>
        </div>
      </nav>

      <main className="page-flow">
        <section id="home" className="hero-block">
          <p className="eyebrow">GLOBAL CONTAINER EXPEDITION</p>
          <h1>
            让每一条链路都在掌控之中
            <span>在深海与数据之间精准航行</span>
          </h1>
          <p className="hero-desc">
            以实时海况、航线优化、舱位调度与风险预警构建新一代海运操作系统。
          </p>
          <div className="hero-cta">
            <a href="#capability" className="btn-solid">查看核心能力</a>
            <a href="#network" className="btn-ghost">进入航线视图</a>
          </div>
        </section>

        <section id="capability" className="content-block">
          <header>
            <p className="eyebrow">CAPABILITY MATRIX</p>
            <h2>为远洋集装箱运输打造的四层能力栈</h2>
          </header>
          <div className="card-grid">
            <article className="info-card">
              <strong>01 航线智能预警</strong>
              <p>结合港口拥堵、天气窗口、燃油模型，动态生成多目标最优航线。</p>
            </article>
            <article className="info-card">
              <strong>02 船司横向协调</strong>
              <p>统一管理船舶、舱位与中转节奏，提升集装箱周转效率。</p>
            </article>
            <article className="info-card">
              <strong>03 风险预警中枢</strong>
              <p>对极端海况、延误链路和成本异常进行分钟级预警。</p>
            </article>
            <article className="info-card">
              <strong>04 全链路可视化</strong>
              <p>从订舱到签收，形成可回放的全链路态势与决策记录。</p>
            </article>
          </div>
        </section>

        <section id="network" className="content-block network-block">
          <header>
            <p className="eyebrow">OCEAN NETWORK</p>
            <h2>穿越深海的集装箱海运网络</h2>
          </header>
          <div className="network-panel">
            <div className="network-depth-light"></div>
            <div className="network-scan-beam"></div>
            <div className="route-line route-1"></div>
            <div className="route-line route-2"></div>
            <div className="route-line route-3"></div>
            <div className="route-packets packets-1">
              <i></i><i></i><i></i>
            </div>
            <div className="route-packets packets-2">
              <i></i><i></i><i></i>
            </div>
            <div className="route-packets packets-3">
              <i></i><i></i><i></i>
            </div>
            <div className="node node-a">上海</div>
            <div className="node node-b">新加坡</div>
            <div className="node node-c">鹿特丹</div>
            <div className="node node-d">洛杉矶</div>
            <div className="node-pulse pulse-a"></div>
            <div className="node-pulse pulse-b"></div>
            <div className="node-pulse pulse-c"></div>
            <div className="node-pulse pulse-d"></div>
            <div className="scan-dot"></div>
          </div>
          <div id="contact" className="network-contact">
            <div className="footer-main">
              <div className="footer-brand">
                <h3>闪石信息科技（上海）有限公司</h3>
                <p>以工程理性与海运场景深度融合，持续推进航运数字化基础设施升级。</p>
              </div>
              <div className="contact-meta">
                <h4>Office</h4>
                <p>Office / 上海 . 杭州</p>
                <p>Email / angela@30tech.net</p>
              </div>
            </div>
            <div className="footer-bottom">
              <span><Copyright /> 闪石信息科技（上海）有限公司</span>
              <div className="record-links">
                <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">沪ICP备15046145号-12</a>
                
                <a className="police-record" href="https://beian.mps.gov.cn/#/query/webSearch?code=31010902101178" target="_blank" rel="noreferrer">
                  <img className="police-badge" src={ga} alt="公安备案图标" />
                  <span>沪公网安备31010902101178号</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;
